// Crew status language: copper = someone is needed, moss = done / working, grey = waiting on
// the outside world. Built on shadcn Badge; tones follow the Paper "Family" designs.
import { Badge } from "@crew/ui/components/badge";
import { cn } from "@crew/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

const statusBadge = cva(
  "h-auto rounded-sm border-transparent px-1.5 py-0.5 text-2xs font-semibold",
  {
    variants: {
      tone: {
        waiting: "bg-copper/10 text-copper",
        done: "bg-moss/10 text-moss",
        working: "bg-foreground/6 text-muted-foreground",
        neutral: "bg-foreground/8 text-foreground",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

/** Small status label ("Waiting on you", "Done", "Working", "Recommended"). */
export function StatusBadge({
  tone,
  className,
  ...props
}: ComponentProps<typeof Badge> & VariantProps<typeof statusBadge>) {
  return (
    <Badge data-slot="status-badge" className={cn(statusBadge({ tone }), className)} {...props} />
  );
}

/** Copper count pill for nav items that need someone. */
export function CountBadge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Badge
      data-slot="count-badge"
      className={cn(
        "h-[18px] min-w-[18px] rounded-full bg-copper px-[7px] py-0 text-xs font-semibold text-white tabular",
        className,
      )}
    >
      {children}
    </Badge>
  );
}

export type StatusTone = "working" | "waiting" | "asleep" | "queued" | "done" | "error";

const dot: Record<StatusTone, string> = {
  working: "bg-moss",
  waiting: "bg-copper",
  asleep: "bg-sleep",
  queued: "bg-foreground/25",
  done: "bg-moss",
  error: "bg-brick",
};

const label: Record<StatusTone, string> = {
  working: "text-moss",
  waiting: "text-copper",
  asleep: "text-muted-foreground",
  queued: "text-muted-foreground",
  done: "text-moss",
  error: "text-brick",
};

/** 8px dot. `hollow` draws a ring for "waiting on someone outside the family". */
export function StatusDot({
  tone,
  hollow = false,
  className,
}: {
  tone: StatusTone;
  hollow?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      data-slot="status-dot"
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        hollow ? "border-[1.5px] border-muted-foreground bg-transparent" : dot[tone],
        className,
      )}
    />
  );
}

/** Dot + text in the matching colour ("Waiting on Marcus"). */
export function StatusLabel({
  tone,
  hollow,
  emphasis = false,
  children,
  className,
}: {
  tone: StatusTone;
  hollow?: boolean;
  emphasis?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="status-label"
      className={cn(
        "inline-flex items-center gap-2 text-meta",
        hollow ? "text-muted-foreground" : label[tone],
        emphasis && "font-medium text-foreground",
        className,
      )}
    >
      <StatusDot tone={tone} hollow={hollow} />
      {children}
    </span>
  );
}
